import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { cloneDeep } from "es-toolkit/object";
import useSupabaseBrowser from "utils/supabaseBrowserClient";
import { QueryHookOptions } from "types/common";
import { getCategoriesDynamicTabs } from "../api";
import { BeforeAfterCategoryData } from "../types";

const useDynamicCategoriesData = (
  categories: BeforeAfterCategoryData[] | undefined = [],
  options?: QueryHookOptions
) => {
  const supabaseClient = useSupabaseBrowser();
  const categoriesWithDynamicTabs = useMemo(
    () => categories.filter((category) => !!category.enableDynamicTabs),
    [categories]
  );

  const { data, isLoading } = useQuery({
    queryKey: ["tile_comp_before_after", ...categoriesWithDynamicTabs.map((category) => category.title)],
    queryFn: () => getCategoriesDynamicTabs(supabaseClient, categoriesWithDynamicTabs),
    refetchOnMount: true,
    refetchInterval: false,
    refetchOnWindowFocus: false,
    enabled: !!categoriesWithDynamicTabs?.length,
    ...options
  });

  const finalCategories = useMemo(() => {
    if (!data?.categories?.length) return categories;
    const finalData = cloneDeep(categories);
    return finalData.map((category) => {
      const dynamicCategory = data.categories.find((item) => item.title === category.title);
      if (dynamicCategory) {
        return {
          ...category,
          tabs: dynamicCategory.tabs
        };
      }
      return category;
    });
  }, [categories, data?.categories]);

  return {
    data: finalCategories,
    isCategoryWithDynamicTabs: !!categoriesWithDynamicTabs.length,
    isLoading
  };
};

export default useDynamicCategoriesData;
